import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { money, dateTimeEs, statusLabel } from "@/lib/formatters";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Save, Trash2, Plus, ArrowLeft, Send, Copy, FileDown, History, ExternalLink, Package, Search,
  RotateCcw, CloudCheck,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

// PRD v1.14 — autoguardado local del borrador de "nueva cotización".
// Si el usuario recarga o cierra la pestaña, el borrador queda en localStorage
// y puede recuperarse al volver a /quotes/new.
const LS_DRAFT_KEY = "pe:quote:new-draft";
const AUTOSAVE_DEBOUNCE_MS = 800;

const isMeaningfulDraft = (q) => {
  if (!q) return false;
  const hasClient = (q.client?.name || "").trim().length > 0 ||
                     (q.client?.rfc || "").trim().length > 0 ||
                     !!q.client?.client_id;
  const hasItems = (q.items || []).some((it) => (it.description || "").trim() ||
                                                  Number(it.unit_price) > 0);
  // Nota: NO se considera `notes` porque se prellena con la plantilla de la
  // empresa y produciría falsos positivos en cotizaciones recién montadas.
  return hasClient || hasItems;
};

export default function QuoteEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [company, setCompany] = useState(null);
  const [clients, setClients] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publicLink, setPublicLink] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);
  const [showCatalogPicker, setShowCatalogPicker] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");

  // PRD v1.14 — autoguardado local (solo cuando estás creando una cotización nueva)
  const [pendingDraft, setPendingDraft] = useState(null); // {quote, savedAt} → modal de recuperación
  const [lastAutosaveAt, setLastAutosaveAt] = useState(null);
  const autosaveTimer = useRef(null);
  const savedToServerRef = useRef(false); // evita restaurar draft tras crear la cotización
  const userTouchedRef = useRef(false); // se activa cuando el usuario modifica algo real

  useEffect(() => {
    (async () => {
      try {
        const [c, cs] = await Promise.all([api.get("/company"), api.get("/clients")]);
        setCompany(c.data);
        setClients(cs.data);
        if (isNew) {
          // Chequea si hay un borrador local pendiente de recuperar
          let recoveredDraft = null;
          try {
            const raw = localStorage.getItem(LS_DRAFT_KEY);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed?.quote && isMeaningfulDraft(parsed.quote)) {
                recoveredDraft = parsed;
              }
            }
          } catch { /* corrupt draft — ignore */ }

          const nf = await api.get("/quotes/next-folio");
          const emptyQuote = {
            folio: nf.data.folio,
            type: "producto",
            status: "borrador",
            client: { client_id: null, name: "", rfc: "", email: "", phone: "", address: "", contact_name: "" },
            items: [{ id: cryptoRnd(), description: "", unit: "PZA", quantity: 1, unit_price: 0, discount_pct: 0 }],
            iva_rate: c.data.iva_rate_default ?? 16,
            isr_rate: c.data.isr_rate_default ?? 0,
            currency: "MXN",
            notes: c.data.default_notes?.producto || "",
            use_default_notes: !!(c.data.default_notes?.producto),
            include_policies: false,
            delivery_time: "",
            validity_days: 15,
            history: [],
          };
          setQuote(emptyQuote);
          if (recoveredDraft) {
            setPendingDraft(recoveredDraft);
          }
        } else {
          const r = await api.get(`/quotes/${id}`);
          setQuote(r.data);
        }
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // PRD v1.14 — autoguardado (debounced) del borrador nuevo en localStorage.
  // Sólo escribe cuando el usuario ha modificado el form (userTouchedRef) para
  // evitar falsos borradores creados por el prellenado inicial de la empresa.
  useEffect(() => {
    if (!isNew || !quote || savedToServerRef.current) return;
    if (!userTouchedRef.current) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      try {
        if (isMeaningfulDraft(quote)) {
          const savedAt = new Date().toISOString();
          localStorage.setItem(LS_DRAFT_KEY, JSON.stringify({ quote, savedAt }));
          setLastAutosaveAt(savedAt);
        } else {
          // borrador vacío → no dejar residuos
          localStorage.removeItem(LS_DRAFT_KEY);
          setLastAutosaveAt(null);
        }
      } catch { /* quota/serialización — ignore */ }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [isNew, quote]);

  // Flush sincrónico al cerrar la pestaña o refrescar (sólo si hubo cambios reales)
  useEffect(() => {
    if (!isNew) return;
    const flush = () => {
      if (savedToServerRef.current) return;
      if (!userTouchedRef.current) return;
      try {
        if (quote && isMeaningfulDraft(quote)) {
          localStorage.setItem(LS_DRAFT_KEY, JSON.stringify({
            quote, savedAt: new Date().toISOString(),
          }));
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", flush);
    return () => window.removeEventListener("beforeunload", flush);
  }, [isNew, quote]);

  const totals = useMemo(() => {
    if (!quote) return { subtotal: 0, iva: 0, isr: 0, total: 0 };
    const subtotal = quote.items.reduce((s, it) => {
      const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * (1 - (Number(it.discount_pct) || 0) / 100);
      return s + line;
    }, 0);
    const iva = subtotal * ((Number(quote.iva_rate) || 0) / 100);
    const isr = subtotal * ((Number(quote.isr_rate) || 0) / 100);
    return { subtotal, iva, isr, total: subtotal + iva - isr };
  }, [quote]);

  // PRD 9.11 — carga catálogo del tipo actual (servicio/producto), solo activos
  useEffect(() => {
    if (!quote?.type) return;
    (async () => {
      try {
        const r = await api.get(`/catalog?kind=${quote.type}&active_only=true`);
        setCatalogItems(r.data || []);
      } catch { setCatalogItems([]); }
    })();
  }, [quote?.type]);

  const filteredCatalog = useMemo(() => {
    if (!catalogSearch) return catalogItems;
    const q = catalogSearch.toLowerCase();
    return catalogItems.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.description || "").toLowerCase().includes(q) ||
      (it.sku || "").toLowerCase().includes(q)
    );
  }, [catalogItems, catalogSearch]);

  if (loading || !quote) {
    return <div className="p-8 text-slate-500">Cargando…</div>;
  }

  const isLocked = ["aceptada", "rechazada", "cerrada"].includes(quote.status);

  const setField = (patch) => { userTouchedRef.current = true; setQuote((q) => ({ ...q, ...patch })); };
  const setItem = (idx, patch) => {
    userTouchedRef.current = true;
    setQuote((q) => ({ ...q, items: q.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  };
  const addItem = () => {
    userTouchedRef.current = true;
    setQuote((q) => ({
      ...q,
      items: [...q.items, { id: cryptoRnd(), description: "", unit: "PZA", quantity: 1, unit_price: 0, discount_pct: 0 }],
    }));
  };
  const removeItem = (idx) => { userTouchedRef.current = true; setQuote((q) => ({ ...q, items: q.items.filter((_, i) => i !== idx) })); };

  const addFromCatalog = (item) => {
    userTouchedRef.current = true;
    setQuote((q) => ({
      ...q,
      items: [...q.items, {
        id: cryptoRnd(),
        description: item.description || item.name,
        unit: item.unit || "PZA",
        quantity: 1,
        unit_price: item.unit_price || 0,
        discount_pct: 0,
        catalog_item_id: item.id,
      }],
    }));
    setShowCatalogPicker(false);
    setCatalogSearch("");
    toast.success(`Agregado: ${item.name}`);
  };

  const applyDefaultNotes = (newType) => {
    if (!company) return;
    const currentDefault = company.default_notes?.[quote.type] || "";
    const newDefault = company.default_notes?.[newType] || "";
    if (quote.notes === currentDefault || !quote.notes) {
      setField({ type: newType, notes: newDefault });
    } else {
      setField({ type: newType });
    }
  };

  const onSave = async () => {
    if (!quote.client.name || quote.client.name.trim().length < 2) {
      toast.error("Selecciona o captura un cliente");
      return;
    }
    if (quote.items.length === 0 || quote.items.every((it) => !it.description)) {
      toast.error("Agrega al menos un ítem con descripción");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: quote.type,
        client: quote.client,
        items: quote.items.map((it) => ({
          description: it.description || "",
          unit: it.unit || "PZA",
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          discount_pct: Number(it.discount_pct) || 0,
          catalog_item_id: it.catalog_item_id || undefined,
        })),
        iva_rate: Number(quote.iva_rate) || 0,
        isr_rate: Number(quote.isr_rate) || 0,
        notes: quote.notes || "",
        delivery_time: quote.delivery_time || "",
        validity_days: Number(quote.validity_days) || 15,
        include_policies: quote.include_policies !== false,
      };
      let r;
      if (isNew) {
        r = await api.post("/quotes", payload);
        // limpia el borrador local (evita restaurar tras crearla)
        savedToServerRef.current = true;
        try { localStorage.removeItem(LS_DRAFT_KEY); } catch { /* ignore */ }
        toast.success(`Cotización ${r.data.folio} creada`);
        navigate(`/quotes/${r.data.id}`, { replace: true });
      } else {
        r = await api.patch(`/quotes/${id}`, payload);
        setQuote(r.data);
        toast.success("Cotización actualizada");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onGeneratePublicLink = async () => {
    try {
      const r = await api.post(`/quotes/${id}/generate-public-link`);
      setPublicLink(r.data);
      setQuote((q) => ({ ...q, public_token: r.data.public_token, status: "enviada" }));
      toast.success("Link público generado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const onDownloadPDF = () => {
    if (!id) return;
    const token = localStorage.getItem("pe_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${quote.folio}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const publicUrlComputed = quote.public_token
    ? `${window.location.origin}/c/${quote.public_token}`
    : null;

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto pb-24 min-w-0 overflow-x-hidden" data-testid="quote-editor-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate("/quotes")}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-[color:var(--pe-primary)]"
                  data-testid="btn-back-quotes">
            <ArrowLeft size={14} /> Volver a cotizaciones
          </button>
          <div className="mt-1 flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-black text-[color:var(--pe-primary)] pe-numeric">{quote.folio}</h1>
            <span className={`pe-badge pe-badge-${quote.status}`}>{statusLabel[quote.status]}</span>
            {isLocked && (
              <span className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 rounded-md">
                Bloqueada · registro histórico
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isNew && (
            <>
              <Button variant="outline" onClick={() => setShowHistory(true)} data-testid="btn-show-history">
                <History size={14} className="mr-1" /> Historial ({quote.history?.length || 0})
              </Button>
              <Button variant="outline" onClick={onDownloadPDF} data-testid="btn-download-pdf">
                <FileDown size={14} className="mr-1" /> PDF
              </Button>
              {!isLocked && (
                <Button variant="outline" onClick={onGeneratePublicLink} data-testid="btn-public-link">
                  <Send size={14} className="mr-1" /> Enviar / Regenerar link
                </Button>
              )}
            </>
          )}
          {!isLocked && (
            <Button onClick={onSave} disabled={saving} className="pe-btn-primary text-white" data-testid="btn-save-quote">
              <Save size={14} className="mr-1" /> {saving ? "Guardando…" : (isNew ? "Crear cotización" : "Guardar cambios")}
            </Button>
          )}
        </div>
      </div>

      {/* PRD v1.14 — indicador de autoguardado (sólo en cotización nueva) */}
      {isNew && lastAutosaveAt && !pendingDraft && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"
             data-testid="autosave-indicator" title={`Guardado localmente a las ${new Date(lastAutosaveAt).toLocaleTimeString("es-MX")}`}>
          <CloudCheck size={13} className="text-emerald-600" />
          Borrador guardado localmente · {new Date(lastAutosaveAt).toLocaleTimeString("es-MX")}
        </div>
      )}

      {/* PRD v1.14 — modal para recuperar borrador si hubo cierre/refresh */}
      <Dialog open={!!pendingDraft} onOpenChange={(o) => { if (!o) setPendingDraft(null); }}>
        <DialogContent className="max-w-md" data-testid="recover-draft-dialog">
          <DialogHeader>
            <DialogTitle className="text-[color:var(--pe-primary)] font-black">
              Continuar borrador pendiente
            </DialogTitle>
            <DialogDescription>
              Detectamos un borrador de cotización sin guardar de{" "}
              {pendingDraft?.savedAt ? new Date(pendingDraft.savedAt).toLocaleString("es-MX") : ""}.{" "}
              ¿Quieres continuarlo o empezar uno nuevo?
            </DialogDescription>
          </DialogHeader>
          {pendingDraft?.quote && (
            <div className="rounded-md bg-slate-50 border border-[color:var(--pe-border)] p-3 text-sm space-y-1">
              <div><b>Cliente:</b> {pendingDraft.quote.client?.name || <i className="text-slate-400">Sin cliente</i>}</div>
              <div><b>Tipo:</b> {pendingDraft.quote.type}</div>
              <div><b>Ítems:</b> {(pendingDraft.quote.items || []).filter((it) => (it.description || "").trim()).length}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline"
                    data-testid="recover-draft-discard"
                    onClick={() => {
                      // cancela cualquier autosave pendiente y limpia el LS
                      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
                      userTouchedRef.current = false;
                      try { localStorage.removeItem(LS_DRAFT_KEY); } catch { /* ignore */ }
                      setPendingDraft(null);
                      setLastAutosaveAt(null);
                      toast.success("Borrador descartado");
                    }}>
              <Trash2 size={14} className="mr-1" /> Descartar
            </Button>
            <Button className="pe-btn-primary text-white"
                    data-testid="recover-draft-restore"
                    onClick={() => {
                      // conserva el folio nuevo que ya trajimos del servidor y aplica el resto del borrador
                      userTouchedRef.current = true;
                      setQuote((cur) => ({ ...(pendingDraft.quote || {}), folio: cur?.folio || pendingDraft.quote?.folio }));
                      setPendingDraft(null);
                      toast.success("Borrador recuperado");
                    }}>
              <RotateCcw size={14} className="mr-1" /> Continuar borrador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {publicUrlComputed && (
        <div className="mt-4 pe-card p-4 bg-sky-50 border-sky-200 flex items-center justify-between gap-3 flex-wrap"
             data-testid="public-link-box">
          <div className="min-w-0 flex-1">
            <div className="pe-label opacity-70">LINK PÚBLICO</div>
            <div className="text-sm truncate">{publicUrlComputed}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline"
                    onClick={() => { navigator.clipboard.writeText(publicUrlComputed); toast.success("Link copiado"); }}
                    data-testid="btn-copy-public"><Copy size={14} className="mr-1" />Copiar</Button>
            <Button size="sm" variant="outline" onClick={() => window.open(publicUrlComputed, "_blank")}
                    data-testid="btn-open-public"><ExternalLink size={14} className="mr-1" />Abrir</Button>
          </div>
        </div>
      )}

      {/* Basic info */}
      <div className="mt-6 pe-card p-6">
        <h2 className="text-lg font-black text-[color:var(--pe-primary)] mb-4">Datos generales</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label className="pe-label">Tipo</Label>
            <Select value={quote.type} onValueChange={applyDefaultNotes} disabled={isLocked}>
              <SelectTrigger className="h-11" data-testid="quote-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="producto">Producto</SelectItem>
                <SelectItem value="servicio">Servicio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="pe-label">Vigencia (días)</Label>
            <Input type="number" className="h-11" min={1} value={quote.validity_days} disabled={isLocked}
                   onChange={(e) => setField({ validity_days: Number(e.target.value) })} data-testid="quote-validity-days" />
          </div>
          <div className="space-y-2">
            <Label className="pe-label">Tiempo de entrega/ejecución</Label>
            <Input className="h-11" value={quote.delivery_time || ""} disabled={isLocked}
                   onChange={(e) => setField({ delivery_time: e.target.value })}
                   placeholder="Ej: 4-6 días hábiles o A, B, C…" data-testid="quote-delivery-time" />
          </div>
        </div>
      </div>

      {/* Client */}
      <div className="mt-4 pe-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-[color:var(--pe-primary)]">Cliente</h2>
          <Select value={quote.client?.client_id || "manual"}
                  onValueChange={(v) => {
                    if (v === "manual") {
                      setField({ client: { ...quote.client, client_id: null } });
                    } else {
                      const c = clients.find((cc) => cc.id === v);
                      if (c) setField({
                        client: {
                          client_id: c.id, name: c.name, rfc: c.rfc || "", email: c.email || "",
                          phone: c.phone || "", address: c.address || "", contact_name: c.contact_name || "",
                        }
                      });
                    }
                  }}
                  disabled={isLocked}>
            <SelectTrigger className="w-[280px]" data-testid="quote-client-select">
              <SelectValue placeholder="Selecciona un cliente existente…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Capturar cliente manualmente</SelectItem>
              {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="pe-label">Nombre / Razón social *</Label>
            <Input className="h-11" value={quote.client?.name || ""} disabled={isLocked}
                   onChange={(e) => setField({ client: { ...quote.client, name: e.target.value } })}
                   data-testid="client-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="pe-label">RFC</Label>
            <Input className="h-11" value={quote.client?.rfc || ""} disabled={isLocked}
                   onChange={(e) => setField({ client: { ...quote.client, rfc: e.target.value.toUpperCase() } })}
                   placeholder="XAXX010101000" data-testid="client-rfc-input" />
          </div>
          <div className="space-y-2">
            <Label className="pe-label">Contacto</Label>
            <Input className="h-11" value={quote.client?.contact_name || ""} disabled={isLocked}
                   onChange={(e) => setField({ client: { ...quote.client, contact_name: e.target.value } })} />
          </div>
          <div className="space-y-2">
            <Label className="pe-label">Teléfono</Label>
            <Input className="h-11" value={quote.client?.phone || ""} disabled={isLocked}
                   onChange={(e) => setField({ client: { ...quote.client, phone: e.target.value } })} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="pe-label">Dirección</Label>
            <Input className="h-11" value={quote.client?.address || ""} disabled={isLocked}
                   onChange={(e) => setField({ client: { ...quote.client, address: e.target.value } })} />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="mt-4 pe-card p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="text-lg font-black text-[color:var(--pe-primary)]">Ítems</h2>
          {!isLocked && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setShowCatalogPicker(true)}
                      data-testid="btn-add-from-catalog"
                      className="border-[#005A91]/30 text-[#005A91]"
                      disabled={catalogItems.length === 0}
                      title={catalogItems.length === 0 ? "Aún no hay ítems en el catálogo para este tipo" : ""}>
                <Package size={14} className="mr-1" /> Del catálogo
                {catalogItems.length > 0 && <span className="ml-1.5 text-[10px] font-black bg-[#005A91]/10 px-1.5 py-0.5 rounded">{catalogItems.length}</span>}
              </Button>
              <Button variant="outline" onClick={addItem} data-testid="btn-add-item">
                <Plus size={14} className="mr-1" /> Personalizado
              </Button>
            </div>
          )}
        </div>
        {/* Vista escritorio: tabla (md+) */}
        <div className="hidden md:block overflow-x-auto max-w-full">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-2 py-2 text-xs uppercase tracking-wide text-slate-600 w-8">#</th>
                <th className="px-2 py-2 text-xs uppercase tracking-wide text-slate-600">Descripción</th>
                <th className="px-2 py-2 text-xs uppercase tracking-wide text-slate-600 w-20">Unidad</th>
                <th className="px-2 py-2 text-xs uppercase tracking-wide text-slate-600 w-20">Cant.</th>
                <th className="px-2 py-2 text-xs uppercase tracking-wide text-slate-600 w-32">P. Unitario</th>
                <th className="px-2 py-2 text-xs uppercase tracking-wide text-slate-600 w-20">Desc %</th>
                <th className="px-2 py-2 text-xs uppercase tracking-wide text-slate-600 w-32 text-right">Importe</th>
                {!isLocked && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {quote.items.map((it, idx) => {
                const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) *
                  (1 - (Number(it.discount_pct) || 0) / 100);
                return (
                  <tr key={it.id || idx} className="border-t border-[color:var(--pe-border)]" data-testid={`item-row-${idx}`}>
                    <td className="px-2 py-2 text-slate-500 align-top pt-4">{idx + 1}</td>
                    <td className="px-2 py-2">
                      <Textarea value={it.description} disabled={isLocked} placeholder="Descripción del producto o servicio"
                             rows={2}
                             onChange={(e) => setItem(idx, { description: e.target.value })}
                             data-testid={`item-desc-${idx}`}
                             className="min-h-[64px] resize-y" />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <Input value={it.unit || "PZA"} disabled={isLocked}
                             onChange={(e) => setItem(idx, { unit: e.target.value })} />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <Input type="number" step="0.01" min={0} value={it.quantity} disabled={isLocked}
                             onChange={(e) => setItem(idx, { quantity: e.target.value })}
                             data-testid={`item-qty-${idx}`} />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <Input type="number" step="0.01" min={0} value={it.unit_price} disabled={isLocked}
                             onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                             data-testid={`item-price-${idx}`} />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <Input type="number" step="0.01" min={0} max={100} value={it.discount_pct} disabled={isLocked}
                             onChange={(e) => setItem(idx, { discount_pct: e.target.value })} />
                    </td>
                    <td className="px-2 py-2 text-right pe-numeric font-bold align-top pt-4">{money(line)}</td>
                    {!isLocked && (
                      <td className="px-2 py-2 align-top">
                        <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 p-1"
                                data-testid={`btn-remove-item-${idx}`}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Vista móvil: tarjetas apiladas (< md) */}
        <div className="md:hidden space-y-4" data-testid="items-mobile-list">
          {quote.items.map((it, idx) => {
            const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) *
              (1 - (Number(it.discount_pct) || 0) / 100);
            return (
              <div key={it.id || idx}
                   className="rounded-lg border border-[color:var(--pe-border)] bg-white p-4 shadow-sm"
                   data-testid={`item-card-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="inline-flex items-center gap-2">
                    <span className="inline-flex h-7 min-w-7 px-2 items-center justify-center rounded-full bg-[color:var(--pe-primary)]/10 text-[color:var(--pe-primary)] text-xs font-black">
                      Ítem {idx + 1}
                    </span>
                  </div>
                  {!isLocked && (
                    <button onClick={() => removeItem(idx)}
                            className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 text-sm font-bold px-3 py-2 rounded-md hover:bg-red-50 active:bg-red-100"
                            data-testid={`btn-remove-item-mobile-${idx}`}
                            aria-label={`Eliminar ítem ${idx + 1}`}>
                      <Trash2 size={16} /> Eliminar
                    </button>
                  )}
                </div>

                <div>
                  <Label className="pe-label">Descripción</Label>
                  <Textarea value={it.description} disabled={isLocked}
                         placeholder="Descripción del producto o servicio"
                         rows={4}
                         onChange={(e) => setItem(idx, { description: e.target.value })}
                         data-testid={`item-desc-mobile-${idx}`}
                         className="mt-1 min-h-[112px] resize-y text-base" />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="pe-label">Cantidad</Label>
                    <Input type="number" inputMode="decimal" step="0.01" min={0}
                           className="mt-1 h-12 text-base"
                           value={it.quantity} disabled={isLocked}
                           onChange={(e) => setItem(idx, { quantity: e.target.value })}
                           data-testid={`item-qty-mobile-${idx}`} />
                  </div>
                  <div>
                    <Label className="pe-label">Unidad</Label>
                    <Input value={it.unit || "PZA"} disabled={isLocked}
                           className="mt-1 h-12 text-base"
                           onChange={(e) => setItem(idx, { unit: e.target.value })}
                           data-testid={`item-unit-mobile-${idx}`} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="pe-label">Precio unitario</Label>
                    <Input type="number" inputMode="decimal" step="0.01" min={0}
                           className="mt-1 h-12 text-base"
                           value={it.unit_price} disabled={isLocked}
                           onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                           data-testid={`item-price-mobile-${idx}`} />
                  </div>
                  <div>
                    <Label className="pe-label">Descuento %</Label>
                    <Input type="number" inputMode="decimal" step="0.01" min={0} max={100}
                           className="mt-1 h-12 text-base"
                           value={it.discount_pct} disabled={isLocked}
                           onChange={(e) => setItem(idx, { discount_pct: e.target.value })}
                           data-testid={`item-discount-mobile-${idx}`} />
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-[color:var(--pe-border)] flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-slate-500 font-bold">Importe</span>
                  <span className="pe-numeric font-black text-lg text-[color:var(--pe-primary)]"
                        data-testid={`item-line-mobile-${idx}`}>
                    {money(line)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="pe-label">IVA %</Label>
                <Input type="number" step="0.01" className="h-11 mt-1" value={quote.iva_rate} disabled={isLocked}
                       onChange={(e) => setField({ iva_rate: Number(e.target.value) })} data-testid="quote-iva-input" />
              </div>
              <div>
                <Label className="pe-label">ISR % (retención, puede ser negativo)</Label>
                <Input type="number" step="0.01" className="h-11 mt-1" value={quote.isr_rate} disabled={isLocked}
                       onChange={(e) => setField({ isr_rate: Number(e.target.value) })} data-testid="quote-isr-input" />
              </div>
            </div>
          </div>
          <div className="space-y-2 pe-card p-4 bg-slate-50">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span className="pe-numeric font-bold">{money(totals.subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span>IVA ({quote.iva_rate || 0}%)</span><span className="pe-numeric font-bold">{money(totals.iva)}</span></div>
            {(quote.isr_rate || 0) !== 0 && (
              <div className="flex justify-between text-sm"><span>ISR ({quote.isr_rate}%)</span><span className="pe-numeric font-bold text-red-600">- {money(Math.abs(totals.isr))}</span></div>
            )}
            <div className="border-t border-[color:var(--pe-border)] mt-2 pt-2 flex justify-between">
              <span className="pe-label">TOTAL</span>
              <span className="pe-numeric font-black text-xl text-[color:var(--pe-primary)]" data-testid="quote-total">{money(totals.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-4 pe-card p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h2 className="text-lg font-black text-[color:var(--pe-primary)]">Notas y condiciones</h2>
        </div>

        <div className="flex items-start gap-3 rounded-md bg-slate-50 border border-[color:var(--pe-border)] p-3 mb-3">
          <Checkbox id="use-default-notes"
                    checked={quote.use_default_notes !== false}
                    disabled={isLocked || !company?.default_notes?.[quote.type]}
                    onCheckedChange={(v) => {
                      const checked = !!v;
                      if (checked) {
                        setField({
                          use_default_notes: true,
                          notes: company?.default_notes?.[quote.type] || "",
                        });
                      } else {
                        setField({ use_default_notes: false, notes: "" });
                      }
                    }}
                    data-testid="quote-use-default-notes-checkbox"
                    className="mt-0.5" />
          <div className="flex-1 min-w-0">
            <label htmlFor="use-default-notes" className="text-sm font-bold text-[color:var(--pe-primary)] cursor-pointer select-none">
              Usar notas predeterminadas ({quote.type})
            </label>
            <p className="text-xs text-slate-500 mt-0.5">
              Al activarlo, se copia la plantilla configurada en <b>Empresa → Notas por tipo</b>. Puedes editarla debajo cuando esté activo.
              {!company?.default_notes?.[quote.type] && (
                <span className="block text-amber-700 mt-1">
                  No hay plantilla configurada para este tipo — configúrala en Empresa.
                </span>
              )}
            </p>
          </div>
        </div>

        <Textarea value={quote.notes || ""} disabled={isLocked || quote.use_default_notes === false}
                  onChange={(e) => setField({ notes: e.target.value })}
                  rows={7} data-testid="quote-notes-textarea"
                  placeholder={quote.use_default_notes === false
                    ? "Notas desactivadas — activa el checkbox para escribir o cargar la plantilla."
                    : "Notas y condiciones de la cotización…"} />
        <p className="text-xs text-slate-500 mt-2">
          {quote.use_default_notes === false
            ? "No se incluirán notas en el PDF ni en el link público."
            : "Las notas se guardan tal como aparecen en el recuadro."}
        </p>

        {/* PRD v1.10 — checkbox para políticas (off por default) */}
        <div className="mt-5 flex items-start gap-3 rounded-md bg-slate-50 border border-[color:var(--pe-border)] p-3">
          <Checkbox id="include-policies"
                    checked={quote.include_policies === true}
                    disabled={isLocked}
                    onCheckedChange={(v) => setField({ include_policies: !!v })}
                    data-testid="quote-include-policies-checkbox"
                    className="mt-0.5" />
          <div className="flex-1 min-w-0">
            <label htmlFor="include-policies" className="text-sm font-bold text-[color:var(--pe-primary)] cursor-pointer select-none">
              Incluir Políticas de entrega y servicio
            </label>
            <p className="text-xs text-slate-500 mt-0.5">
              Desactivado por default. Al activarlo, el PDF y el link público al final muestran la sección de <b>Políticas de entrega y servicio</b> (garantías, plazos, condiciones).
            </p>
          </div>
        </div>
      </div>

      {(quote.accepted_at || quote.rejected_at) && (
        <div className={`mt-4 pe-card p-4 ${quote.accepted_at ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
          <div className="pe-label">EVIDENCIA DE RESPUESTA</div>
          <div className="text-sm mt-1">
            <b>{quote.accepted_at ? "Aceptada" : "Rechazada"}</b> el {dateTimeEs(quote.accepted_at || quote.rejected_at)} desde IP <code>{quote.accepted_ip || quote.rejected_ip}</code> · dispositivo: {quote.accepted_device || quote.rejected_device}
          </div>
        </div>
      )}

      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial de cambios</DialogTitle>
            <DialogDescription>Todo cambio queda registrado dentro del mismo documento (nunca se duplica el folio).</DialogDescription>
          </DialogHeader>
          <div className="mt-3 space-y-3">
            {(quote.history || []).length === 0 && <div className="text-sm text-slate-500">Sin registros.</div>}
            {(quote.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="border-l-4 border-[color:var(--pe-accent)] pl-3 py-2 bg-slate-50 rounded-r-md">
                <div className="text-sm font-bold text-[color:var(--pe-primary)] capitalize">{h.action}</div>
                <div className="text-xs text-slate-500">{dateTimeEs(h.timestamp)} · {h.user_name || "sistema"}</div>
                {h.changes && Object.keys(h.changes).length > 0 && (
                  <div className="mt-1 text-xs text-slate-600">
                    {Object.keys(h.changes).map((k) => (
                      <div key={k}><b>{k}</b>: {JSON.stringify(h.changes[k])}</div>
                    ))}
                  </div>
                )}
                {h.note && <div className="text-xs italic text-slate-500 mt-1">{h.note}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Catalog picker dialog — PRD 9.11 */}
      <Dialog open={showCatalogPicker} onOpenChange={setShowCatalogPicker}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col p-0" data-testid="catalog-picker">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="text-[color:var(--pe-primary)] font-black flex items-center gap-2">
              <Package className="h-5 w-5" /> Elige del catálogo
            </DialogTitle>
            <DialogDescription>
              Se prellenan descripción, unidad y precio. Cantidad y descuento se editan por línea sin tocar el catálogo maestro.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-3 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)}
                     data-testid="catalog-picker-search" autoFocus
                     placeholder="Buscar por nombre, descripción o SKU…" className="pl-8 h-10" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-2">
            {filteredCatalog.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">
                {catalogItems.length === 0
                  ? `Aún no hay ${quote.type === "servicio" ? "servicios" : "productos"} en el catálogo. Da de alta desde el módulo Catálogo.`
                  : "Sin coincidencias con tu búsqueda."}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredCatalog.map((it) => (
                  <button key={it.id} onClick={() => addFromCatalog(it)}
                          data-testid={`catalog-pick-${it.id}`}
                          className="w-full text-left py-3 px-2 hover:bg-slate-50 rounded flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-bold text-[color:var(--pe-primary)] truncate">{it.name}</div>
                      {it.description && <div className="text-xs text-slate-600 line-clamp-2">{it.description}</div>}
                      <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">
                        {it.unit}{it.sku ? ` · ${it.sku}` : ""}{it.times_used > 0 ? ` · usado ${it.times_used}x` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="pe-numeric font-black text-[color:var(--pe-primary)]">{money(it.unit_price)}</div>
                      <div className="text-[10px] text-slate-500">precio base</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function cryptoRnd() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "id-" + Math.random().toString(36).slice(2);
}
